import { useEffect } from 'react';

const SITE_NAME = 'For Visibility';
const TITLE_SUFFIX = ` — ${SITE_NAME}`;

interface PageMeta {
  title: string;
  description: string;
  /** Override the default title suffix. Pass '' to suppress entirely. */
  titleSuffix?: string;
  /** Canonical URL path, e.g. '/pricing'. Omit for the current pathname. */
  canonicalPath?: string;
  /**
   * OG image page slug, e.g. 'pricing'. Resolves to `/api/og?page=${ogPage}`.
   * Ignored when an explicit `ogImage` is provided. Defaults to 'landing'.
   */
  ogPage?: string;
  /**
   * Fully-formed OG image path, e.g. '/api/og?page=landing'. Takes precedence
   * over `ogPage`. Defaults to the landing OG when neither is provided.
   */
  ogImage?: string;
}

/**
 * Lightweight per-page SEO. Sets document.title and injects/updates
 * a canonical link and meta tags. No external dependencies.
 */
export function usePageMeta({
  title,
  description,
  titleSuffix = TITLE_SUFFIX,
  canonicalPath,
  ogPage,
  ogImage = ogPage ? `/api/og?page=${ogPage}` : '/api/og?page=landing',
}: PageMeta) {
  useEffect(() => {
    const fullTitle = title + (titleSuffix ?? '');
    document.title = fullTitle;

    // Description
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.setAttribute('name', 'description');
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute('content', description);

    // OG tags
    setMetaProperty('og:title', fullTitle);
    setMetaProperty('og:description', description);
    setMetaProperty('og:image', ogImage);
    setMetaProperty('og:url', window.location.href);
    setMetaProperty('twitter:title', fullTitle);
    setMetaProperty('twitter:description', description);

    // Canonical
    const href = canonicalPath
      ? `${window.location.origin}${canonicalPath}`
      : (window.location.href.split('?')[0] ?? window.location.href);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement('link');
      canon.setAttribute('rel', 'canonical');
      document.head.appendChild(canon);
    }
    canon.setAttribute('href', href);

    return () => {
      // Restore base title on unmount
      document.title = SITE_NAME;
    };
  }, [title, description, titleSuffix, canonicalPath, ogImage]);
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
